import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useFeeStructures = () => {
  const navigate = useNavigate();
  const [feeStructures, setFeeStructures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchFeeStructures = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {
          page: params.page || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
        };

        // Only add filters that have valid values
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.feeStructures}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setFeeStructures(Array.isArray(data) ? data : data.results || []);
          if (data.count) {
            setPagination((prev) => ({ ...prev, total: data.count }));
          }
        } else {
          Utilities.showError(response, "Failed to fetch fee structures");
        }
      } catch (error) {
        message.error("An error occurred while fetching fee structures");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate, pagination.current, pagination.pageSize],
  );

  const createFeeStructure = useCallback(
    async (data) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.feeStructures,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Fee structure created successfully");
          fetchFeeStructures({ page: 1 });
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create fee structure");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while creating fee structure");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeStructures],
  );

  const updateFeeStructure = useCallback(
    async (id, data) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "PUT",
          `${domainSettings.urls.finance.feeStructures}/${id}`,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Fee structure updated successfully");
          fetchFeeStructures();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update fee structure");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while updating fee structure");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeStructures],
  );

  const deleteFeeStructure = useCallback(
    async (id) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          `${domainSettings.urls.finance.feeStructures}/${id}`,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Fee structure deleted successfully");
          fetchFeeStructures();
          return true;
        } else {
          const errorMsg =
            response.msg ||
            response.data?.error ||
            "Failed to delete fee structure";
          message.error(errorMsg);
          return false;
        }
      } catch (error) {
        message.error("An error occurred while deleting fee structure");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchFeeStructures],
  );

  const activateFeeStructure = useCallback(
    async (id) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          `${domainSettings.urls.finance.feeStructures}/${id}/activate`,
          {},
          navigate,
        );

        if (response.ok) {
          const isNowActive = response.data?.isActive === 1;
          if (isNowActive) {
            message.success(
              `Fee structure activated. ${response.data?.studentsAssigned || 0} students assigned`,
            );
          } else {
            message.success(
              response.data?.message || "Fee structure deactivated",
            );
          }
          fetchFeeStructures();
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to toggle fee structure status",
          );
          return null;
        }
      } catch (error) {
        message.error("An error occurred while toggling fee structure");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeStructures],
  );

  const deactivateFeeStructure = useCallback(
    async (id) => {
      try {
        // Uses the same activate endpoint - backend toggles based on current state
        const response = await Utilities.fetchWithAuth(
          "POST",
          `${domainSettings.urls.finance.feeStructures}/${id}/activate`,
          {},
          navigate,
        );

        if (response.ok) {
          const isNowActive = response.data?.isActive === 1;
          if (isNowActive) {
            message.success(
              `Fee structure activated. ${response.data?.studentsAssigned || 0} students assigned`,
            );
          } else {
            message.success(
              response.data?.message || "Fee structure deactivated",
            );
          }
          fetchFeeStructures();
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to toggle fee structure status",
          );
          return null;
        }
      } catch (error) {
        message.error("An error occurred while toggling fee structure");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchFeeStructures],
  );

  const fetchStudentsAssignedToFeeStructure = useCallback(
    async (feeStructureId) => {
      try {
        // Single optimized API call - no more N+1 queries!
        const url =
          domainSettings.urls.finance.feeStructureAssignedStudents.replace(
            ":id",
            feeStructureId,
          );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const students = Array.isArray(response.data) ? response.data : [];
          // Map to consistent format for frontend
          return students.map((student) => ({
            spc: student.spc,
            spcCode: student.spc,
            firstName: student.firstName,
            lastName: student.lastName,
            className: student.className,
            amountDue: student.amountDue,
            amountPaid: student.amountPaid,
            balance: student.balance,
            status: student.status,
            assignmentId: student.assignmentId,
            termName: student.termName,
            dayBoarding: student.dayBoarding,
            acadyr: student.acadyr,
          }));
        } else {
          Utilities.showError(response, "Failed to fetch assigned students");
          return [];
        }
      } catch (error) {
        console.error(
          "Error fetching students assigned to fee structure:",
          error,
        );
        message.error("An error occurred while fetching assigned students");
        return [];
      }
    },
    [navigate],
  );

  return {
    feeStructures,
    loading,
    pagination,
    setPagination,
    fetchFeeStructures,
    createFeeStructure,
    updateFeeStructure,
    deleteFeeStructure,
    activateFeeStructure,
    deactivateFeeStructure,
    fetchStudentsAssignedToFeeStructure,
  };
};
